import { v4 as uuidv4 } from 'uuid';

import { Event, EventType } from '@cashier-integration/model';

interface EventBusOptions {
  enableLogging?: boolean;
}

export type EventHandler<T = any> = (event: Event<T>) => void;
export type Unsubscribe = () => void;

interface EventSubscription {
  id: string;
  eventType: string;
  handler: EventHandler;
  once?: boolean;
}

export class EventBus {
  private readonly subscriptions = new Map<string, EventSubscription[]>();
  private readonly options: EventBusOptions;

  constructor(options: EventBusOptions = {}) {
    this.options = {
      enableLogging: false,
      ...options,
    };
  }

  on<T = any>(
    eventType: EventType | '*',
    handler: EventHandler<T>,
  ): Unsubscribe {
    const subscription: EventSubscription = {
      id: uuidv4(),
      eventType,
      handler: handler as EventHandler,
    };

    const subs = this.subscriptions.get(eventType) || [];
    subs.push(subscription);
    this.subscriptions.set(eventType, subs);

    if (this.options.enableLogging) {
      /* eslint-disable no-console */
      console.log(`[EventBus] Subscribed to: ${eventType}`, subscription.id);
    }

    return () => this.off(subscription.id);
  }

  emit<T = any>(
    event: Event<T>,
    options?: { skipWildcardSubscribers?: boolean },
  ): void {
    if (this.options.enableLogging) {
      /* eslint-disable no-console */
      console.log(`[EventBus] Emitting: ${event.type}`, event);
    }

    const specificSubs = this.subscriptions.get(event.type) || [];
    const wildcardSubs = options?.skipWildcardSubscribers
      ? []
      : this.subscriptions.get('*') || [];

    const subs = [...specificSubs, ...wildcardSubs];

    for (const sub of subs) {
      try {
        sub.handler(event);
      } catch (error) {
        if (this.options.enableLogging) {
          /* eslint-disable no-console */
          console.error(
            `[EventBus] Error handling event: ${event.type}`,
            error,
          );
        }
      }
    }
  }

  off(subscriptionId: string): void {
    for (const [eventType, subs] of this.subscriptions.entries()) {
      const filtered = subs.filter((sub) => sub.id !== subscriptionId);
      if (filtered.length > 0) {
        this.subscriptions.set(eventType, filtered);
      } else {
        this.subscriptions.delete(eventType);
      }
    }
  }
}
